import { mapValues, remove } from "lodash-es";
import { ErrorCode, JSONCodec, headers } from "nats.ws";

import { DefaultNatsCaller } from "@/backend/natsstubs/NatsCaller";
import { callContextFactory } from "@/backend/natsstubs/request";
import { snakeToCamel } from "@/backend/natsstubs/transform";
import { captureException, captureMessage } from "@/error/sentry";
import { goToLogout } from "@/router/navigation";
import { useClientStore } from "@/store/client";
import { formatState, useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";
import type { UnknownRecord } from "@/utils/type";

import { BackendError } from "../BackendError";
import type { Connection } from "../Connection";
import type {
  BackendReadSession,
  SubscriptionHandler,
  SubscriptionLevel,
} from "../read/BackendReadSession";
import { EventInfo } from "../read/EventInfo";
import type { BackendWriteSession } from "../write/BackendWriteSession";
import { BaseBackendSession } from "./BaseBackendSession";
import { NatsCallerWrapper } from "./NatsCallerWrapper";
import type { RpcBackend } from "./RpcBackend";

export class NatsBackendSession
  extends BaseBackendSession
  implements BackendReadSession, BackendWriteSession
{
  private readonly codec = JSONCodec();
  private readonly userId;
  private loggingOut = false;

  constructor(
    private connection: Connection,
    user: { company: string; id: string },
    rpcBackend: RpcBackend,
  ) {
    super(
      user.company,
      rpcBackend,
      new NatsCallerWrapper(
        new DefaultNatsCaller(
          connection,
          user.company,
          user.id,
          callContextFactory((context) => {
            context.correlationId = useClientStore().newCorrelationId();
            // Override default timeout from 15s to 30s (with 4 retries = 2 minutes total)
            if (context.requestTimeout === 15000) {
              context.requestTimeout = 30000;
            }
          }),
        ),
      ),
    );
    this.userId = user.id;
    connection.onError = (msg) => {
      const conn = useConnectionStore();
      captureMessage(`NATS error: ${msg}`, {
        subscriptions: {
          ...mapValues(this.subscriptions, (levelSubs) => levelSubs.map((sub) => sub.getSubject())),
          RPC: rpcBackend.calls,
        },
        connection: {
          lastFreeze: conn.lastFrozen > 0 ? new Date(conn.lastFrozen).toISOString() : undefined,
          lastUnfreeze:
            conn.lastUnfrozen > 0 ? new Date(conn.lastUnfrozen).toISOString() : undefined,
          msSinceLastUnfreeze: conn.lastUnfrozen > 0 ? Date.now() - conn.lastUnfrozen : undefined,
          states: conn.wsStates.get().map(formatState),
        },
      });
    };
  }

  doSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>, level: SubscriptionLevel) {
    try {
      const subscription = this.connection.subscribe(path, {
        callback: (error, msg) => {
          if (error) {
            if (!this.loggingOut) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
              this.loggingOut = error.code === ErrorCode.PermissionsViolation;
              try {
                captureException(error, { subscriptions: { origin: "subscription", topic: path } });
              } finally {
                if (this.loggingOut) {
                  void goToLogout("cannot-subscribe");
                }
              }
            }
            return;
          }
          if (msg.headers?.get?.("clientId") === useClientStore().clientId) {
            // Ignore the messages we sent ourselves
            return;
          }
          useStatisticsStore().addOk("receives");
          // TODO casting to TArgs is not safe
          const body = this.codec.decode(msg.data) as { args: TArgs; string: unknown };
          const { args, ...kwargs } = body;
          const details = new EventInfo(msg.subject);
          void handler(args, kwargs, details);
        },
      });
      this.subscriptions[level].push(subscription);
      return {
        unsubscribe: () => {
          void this.unsubscribe(subscription).then(() =>
            remove(this.subscriptions[level], (sub) => sub.getID() === subscription.getID()),
          );
        },
      };
    } catch (error) {
      throw new BackendError("Subscribe Error", `Error subscribing ${path}`, {
        request: { path },
        response: { error },
      });
    }
  }

  override doUnsubscribeLevel(level: SubscriptionLevel, connectionOpen: boolean) {
    return super.doUnsubscribeLevel(level, !this.connection.isClosed() && connectionOpen);
  }

  almSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    if (this.almSyncId) {
      this.doSubscribe(this.almTopic(path, { subscribe: true }), handler, "session");
    }
  }

  roomSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    this.doSubscribe(this.roomTopic(path, { subscribe: true }), handler, "room");
  }

  pubsubSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    const transformingHandler: SubscriptionHandler<TArgs> = (args, kwargs, event) => {
      const transformedArgs = snakeToCamel(args) as TArgs;
      const transformedKwargs = snakeToCamel(kwargs);
      return handler(transformedArgs, transformedKwargs, event);
    };
    return this.doSubscribe(this.pubsubTopic(path), transformingHandler, "session");
  }

  sessionSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    this.doSubscribe(this.sessionTopic(path, { subscribe: true }), handler, "session");
  }

  boardSubscribe<TArgs>(
    boardId: string,
    path: string,
    level: SubscriptionLevel,
    handler: SubscriptionHandler<TArgs>,
  ) {
    const topic = this.sessionBoardTopic(boardId, path, { subscribe: true });
    this.doSubscribe(topic, handler, level);
  }

  sessionPublish(path: string, value: unknown[]) {
    this.doPublish(this.sessionTopic(path, { subscribe: false }), value);
  }

  boardPublish(boardId: string, path: string, value: unknown[]) {
    if (!boardId) {
      captureMessage("Publish with empty boardId", { info: { path, boardId } });
      return;
    }
    this.doPublish(this.sessionBoardTopic(boardId, path, { subscribe: false }), value);
  }

  boardCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.rpcBackend.boardCall(path, args ?? [], kwargs);
  }

  stickyCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.rpcBackend.stickyCall(path, args ?? [], kwargs);
  }

  private doPublish(topic: string, value: unknown[]) {
    if (this.connection.isClosed()) {
      return;
    }
    try {
      const callHeaders = headers();
      callHeaders.append("correlation-id", useClientStore().newCorrelationId());
      callHeaders.append("clientId", useClientStore().clientId);

      this.connection.publish(topic, this.codec.encode({ args: value }), { headers: callHeaders });
      useStatisticsStore().addOk("publishes");
    } catch (error) {
      useStatisticsStore().addFail("publishes", error);
      throw error;
    }
  }

  private basePath(module: string, subscribe = true) {
    return `piplanning.${module}.6.${this.company}.${subscribe ? "*" : this.userId}`;
  }

  private sessionBoardTopic(boardId: string, path: string, { subscribe = true } = {}) {
    const base = this.basePath("board", subscribe);
    boardId = boardId === "" ? "*" : boardId;
    return `${base}.${this.sessionId}.${boardId}.${path}`;
  }

  private roomTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("session", subscribe)}.${path}`;
  }

  private sessionTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("session", subscribe)}.${this.sessionId}.${path}`;
  }

  private almTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("almsession", subscribe)}.${this.almSyncId}.${path}`;
  }

  private pubsubTopic(path: string) {
    return `pip.${this.company}.pubsub.${this.sessionId}.${path}`;
  }
}
